// scene.js — renderer, scene, camera + resize handling
// The canvas now lives INSIDE the .helixArea sticky viewport (not body-fixed),
// so the whole WebGL experience scrolls as one pinned section of the page.
import * as THREE from 'three';

export function createStage(container = document.body) {
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  // z-index 3: above the sticky section's deco layer (z1/z2), below the
  // DOM "MOTION" line (z4) — the in-scene title handles the real weaving.
  renderer.domElement.style.cssText =
    'position:absolute;inset:0;width:100%;height:100%;z-index:3;pointer-events:none;';
  container.appendChild(renderer.domElement);

  const scene = new THREE.Scene();

  // wider FOV + closer dolly → stronger near/far perspective as the ribbon
  // rotates (subject size kept roughly constant by moving in proportionally)
  // aspect is a placeholder — resize() sets the real one before the first draw.
  const camera = new THREE.PerspectiveCamera(62, 1, 0.05, 100);
  camera.position.set(2.0, 1.7, 9.5);
  camera.lookAt(0, 0.25, 0);

  // Size from the CONTAINER, never window.innerWidth: the sticky box is a block
  // element so its width excludes the scrollbar, and using the window's width
  // would stretch the drawing buffer against its CSS box.
  // setSize's updateStyle=false keeps the inline width/height:100% set above —
  // letting it write px values would overflow the container's overflow:hidden.
  function resize(w, h) {
    if (!w || !h) return;
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h, false);
  }

  // ResizeObserver instead of a debounced window resize: it also catches
  // layout-driven size changes, and there is no stretched interim frame.
  const observer = new ResizeObserver(([entry]) => {
    const { width, height } = entry.contentRect;
    resize(width, height);
    render();
  });
  observer.observe(container);

  // synchronous first sizing — the observer's initial callback lands too late
  resize(container.clientWidth, container.clientHeight);

  function render() { renderer.render(scene, camera); }

  function dispose() {
    observer.disconnect();
    renderer.dispose();
    renderer.domElement.remove();
  }

  return { renderer, scene, camera, render, dispose };
}
